import { BaseEffect } from '../BaseEffect.js';

/**
 * Halftone effect implementation
 * Creates a dynamic halftone pattern with configurable thresholds and animations
 */
export class HalftoneEffect extends BaseEffect {
    constructor(shaderManager) {
        const defaultParams = {
            scale: 1.0,           // Scale of the halftone pattern (smaller = bigger dots)
            dotSize: 0.4,         // Size of the halftone dots
            angleR: 15.0,         // Angle for red channel (degrees)
            angleG: 75.0,         // Angle for green channel (degrees)
            angleB: 30.0,         // Angle for blue channel (degrees)
            angleK: 45.0,         // Angle for black channel (degrees)
            thresholdSteps: 5,    // Number of color steps (1-10)
            colorAmount: 0.8,     // Amount of original color to preserve (0-1)
            blackAmount: 0.3,     // Amount of black dots to mix in (0-1)
            sharpness: 1.0,       // Sharpness of the dots (0-2)
            animSpeed: 0.5,       // Speed of pattern animation
            animAmount: 0.1,      // Amount of pattern animation
            animate: true         // Whether to animate the effect
        };

        super('halftone', shaderManager, defaultParams);
    }

    async init() {
        const vertexSource = `
            attribute vec2 a_position;
            attribute vec2 a_texCoord;
            varying vec2 v_texcoord;
            
            void main() {
                gl_Position = vec4(a_position, 0, 1);
                v_texcoord = a_texCoord;
            }
        `;

        const fragmentSource = `
            precision highp float;
            
            uniform sampler2D u_image;
            uniform float u_time;
            uniform vec2 u_resolution;
            uniform float u_scale;
            uniform float u_dotSize;
            uniform float u_angleR;
            uniform float u_angleG;
            uniform float u_angleB;
            uniform float u_angleK;
            uniform float u_thresholdSteps;
            uniform float u_colorAmount;
            uniform float u_blackAmount;
            uniform float u_sharpness;
            uniform float u_animSpeed;
            uniform float u_animAmount;
            
            varying vec2 v_texcoord;

            const float PI = 3.14159265359;

            // Rotation matrix for halftone patterns
            mat2 rotate2d(float angle) {
                float s = sin(angle);
                float c = cos(angle);
                return mat2(c, -s, s, c);
            }

            // Get halftone pattern value for a single channel
            float getHalftoneChannel(vec2 uv, float angle, float scale, float value) {
                // Calculate base scale based on screen resolution
                float baseScale = min(u_resolution.x, u_resolution.y);
                float pixelScale = (baseScale / 100.0) * scale;
                
                // Rotate UV coordinates
                vec2 rotatedUV = rotate2d(angle * PI / 180.0) * uv;
                
                // Scale the coordinates for dot pattern
                rotatedUV *= pixelScale;
                
                // Add animation
                float anim = u_time * u_animSpeed;
                rotatedUV += vec2(
                    sin(anim + rotatedUV.y * 0.2) * u_animAmount,
                    cos(anim + rotatedUV.x * 0.2) * u_animAmount
                );
                
                // Calculate dot pattern
                vec2 center = floor(rotatedUV) + 0.5;
                float dist = distance(rotatedUV, center);
                
                // Calculate dynamic dot size
                float baseDotSize = 0.5 * u_dotSize;
                float dynamicSize = baseDotSize * (0.2 + value * 0.8);
                
                // Add size animation
                if (u_animAmount > 0.0) {
                    dynamicSize *= 1.0 + sin(anim * 0.5) * u_animAmount * 0.2;
                }
                
                // Create dot with sharp edges
                float sharpness = max(0.01, min(0.5, 0.1 / u_sharpness));
                float pattern = smoothstep(dynamicSize, dynamicSize * (1.0 - sharpness), dist);
                
                return pattern;
            }

            // Quantize a value into steps
            float quantize(float value, float steps) {
                return floor(value * steps + 0.5) / steps;
            }

            void main() {
                vec2 uv = v_texcoord;
                float aspect = u_resolution.x / u_resolution.y;
                
                // Center and scale UV coordinates
                vec2 scaledUV = (uv - 0.5) * vec2(aspect, 1.0);
                
                // Sample original color
                vec4 texColor = texture2D(u_image, uv);
                
                // Convert to luminance for black channel
                float luminance = dot(texColor.rgb, vec3(0.299, 0.587, 0.114));
                
                // Quantize colors into steps
                vec3 quantizedColor = vec3(
                    quantize(texColor.r, u_thresholdSteps),
                    quantize(texColor.g, u_thresholdSteps),
                    quantize(texColor.b, u_thresholdSteps)
                );
                float quantizedLum = quantize(luminance, u_thresholdSteps);
                
                // Generate halftone patterns for each channel
                float r = getHalftoneChannel(scaledUV, u_angleR, u_scale, quantizedColor.r);
                float g = getHalftoneChannel(scaledUV, u_angleG, u_scale, quantizedColor.g);
                float b = getHalftoneChannel(scaledUV, u_angleB, u_scale, quantizedColor.b);
                float k = getHalftoneChannel(scaledUV, u_angleK, u_scale, 1.0 - quantizedLum);
                
                // Mix color and black patterns
                vec3 color = vec3(r, g, b) * quantizedColor;
                vec3 black = vec3(1.0 - k * u_blackAmount);
                
                // Combine patterns with original color
                vec3 halftone = mix(black, color, u_colorAmount);
                vec3 final = mix(quantizedColor, halftone, u_colorAmount);
                
                gl_FragColor = vec4(final, texColor.a);
            }
        `;

        await super.init(vertexSource, fragmentSource);
    }

    setupUniforms() {
        super.setupUniforms();
        
        this.addUniform('u_scale', { type: '1f' });
        this.addUniform('u_dotSize', { type: '1f' });
        this.addUniform('u_angleR', { type: '1f' });
        this.addUniform('u_angleG', { type: '1f' });
        this.addUniform('u_angleB', { type: '1f' });
        this.addUniform('u_angleK', { type: '1f' });
        this.addUniform('u_thresholdSteps', { type: '1f' });
        this.addUniform('u_colorAmount', { type: '1f' });
        this.addUniform('u_blackAmount', { type: '1f' });
        this.addUniform('u_sharpness', { type: '1f' });
        this.addUniform('u_animSpeed', { type: '1f' });
        this.addUniform('u_animAmount', { type: '1f' });
    }

    updateUniforms(uniforms) {
        super.updateUniforms(uniforms);
        
        Object.assign(uniforms, {
            u_scale: { value: this.params.scale, type: '1f' },
            u_dotSize: { value: this.params.dotSize, type: '1f' },
            u_angleR: { value: this.params.angleR, type: '1f' },
            u_angleG: { value: this.params.angleG, type: '1f' },
            u_angleB: { value: this.params.angleB, type: '1f' },
            u_angleK: { value: this.params.angleK, type: '1f' },
            u_thresholdSteps: { value: this.params.thresholdSteps, type: '1f' },
            u_colorAmount: { value: this.params.colorAmount, type: '1f' },
            u_blackAmount: { value: this.params.blackAmount, type: '1f' },
            u_sharpness: { value: this.params.sharpness, type: '1f' },
            u_animSpeed: { value: this.params.animSpeed, type: '1f' },
            u_animAmount: { value: this.params.animAmount, type: '1f' }
        });

        if (!this.params.animate) {
            uniforms.u_time.value = 0;
        }
    }

    getUIConfig() {
        return {
            scale: {
                min: 0.2,
                max: 3.0,
                step: 0.1,
                label: 'Pattern Scale'
            },
            dotSize: {
                min: 0.1,
                max: 1.0,
                step: 0.05,
                label: 'Dot Size'
            },
            angleR: {
                min: 0,
                max: 360,
                step: 5,
                label: 'Red Angle'
            },
            angleG: {
                min: 0,
                max: 360,
                step: 5,
                label: 'Green Angle'
            },
            angleB: {
                min: 0,
                max: 360,
                step: 5,
                label: 'Blue Angle'
            },
            angleK: {
                min: 0,
                max: 360,
                step: 5,
                label: 'Black Angle'
            },
            thresholdSteps: {
                min: 1,
                max: 10,
                step: 1,
                label: 'Color Steps'
            },
            colorAmount: {
                min: 0,
                max: 1,
                step: 0.1,
                label: 'Color Amount'
            },
            blackAmount: {
                min: 0,
                max: 1,
                step: 0.1,
                label: 'Black Amount'
            },
            sharpness: {
                min: 0.1,
                max: 2.0,
                step: 0.1,
                label: 'Dot Sharpness'
            },
            animSpeed: {
                min: 0,
                max: 2,
                step: 0.1,
                label: 'Animation Speed'
            },
            animAmount: {
                min: 0,
                max: 0.5,
                step: 0.01,
                label: 'Animation Amount'
            },
            animate: {
                label: 'Animate'
            }
        };
    }
} 